// Zayna Home — product page controllers.
(() => {
  'use strict';

  if (window.__zhProductInit) return;
  window.__zhProductInit = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const safeJson = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  };

  const clamp = (value, minimum, maximum) => (
    Math.min(maximum, Math.max(minimum, value))
  );

  const setHidden = (element, hidden) => {
    if (element) element.hidden = Boolean(hidden);
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const visibleFocusables = (container) => Array.from(
    container?.querySelectorAll(focusableSelector) || []
  ).filter((element) => (
    !element.hidden &&
    !element.closest('[hidden]') &&
    !element.closest('[inert]') &&
    element.offsetParent !== null
  ));

  /* ------------------------------------------------------------------ */
  /* 0. Product information accordions                                  */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('[data-single-accordion]').forEach((group) => {
    if (group.dataset.singleAccordionReady === 'true') return;

    group.dataset.singleAccordionReady = 'true';
    const rows = Array.from(group.querySelectorAll(':scope > details'));

    rows.forEach((row) => {
      row.addEventListener('toggle', () => {
        if (!row.open) return;

        rows.forEach((otherRow) => {
          if (otherRow !== row && otherRow.open) {
            otherRow.open = false;
          }
        });
      });
    });
  });

  /* ------------------------------------------------------------------ */
  /* 1. Product gallery                                                 */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const images = safeJson(gallery.getAttribute('data-images') || '[]', []);
    if (!images.length) return;

    const track = gallery.querySelector('[data-gallery-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const mainImage = gallery.querySelector('.zh-gallery__main-image');
    const thumbRail = gallery.querySelector('.zh-gallery__thumbs');
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    const segments = Array.from(gallery.querySelectorAll('[data-gallery-segment]'));
    const counters = Array.from(gallery.querySelectorAll('[data-gallery-counter]'));
    const previousButtons = Array.from(gallery.querySelectorAll('[data-gallery-prev]'));
    const nextButtons = Array.from(gallery.querySelectorAll('[data-gallery-next]'));
    const openButtons = Array.from(gallery.querySelectorAll('[data-gallery-viewer-open]'));

    let activeIndex = 0;
    let scrollFrame = 0;

    const scrollBehavior = (instant = false) => (
      instant || reducedMotion ? 'auto' : 'smooth'
    );

    const revealActiveThumb = (instant = false) => {
      const thumb = thumbs[activeIndex];
      if (!thumbRail || !thumb) return;

      const railLeft = thumbRail.scrollLeft;
      const railRight = railLeft + thumbRail.clientWidth;
      const thumbLeft = thumb.offsetLeft;
      const thumbRight = thumbLeft + thumb.offsetWidth;
      const edgePadding = 3;

      let nextLeft = null;

      if (thumbLeft < railLeft + edgePadding) {
        nextLeft = thumbLeft - edgePadding;
      } else if (thumbRight > railRight - edgePadding) {
        nextLeft = thumbRight - thumbRail.clientWidth + edgePadding;
      }

      if (nextLeft === null) return;

      thumbRail.scrollTo({
        left: Math.max(0, nextLeft),
        behavior: scrollBehavior(instant),
      });
    };

    const setNavigationState = () => {
      const atStart = activeIndex <= 0;
      const atEnd = activeIndex >= images.length - 1;

      previousButtons.forEach((button) => {
        button.disabled = atStart;
      });

      nextButtons.forEach((button) => {
        button.disabled = atEnd;
      });
    };

    const updateIndicators = () => {
      const current = activeIndex + 1;

      counters.forEach((counter) => {
        counter.textContent = `${current} / ${images.length}`;
      });

      segments.forEach((segment, index) => {
        segment.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });

      gallery.setAttribute(
        'aria-label',
        `Productgalerij, afbeelding ${current} van ${images.length}. Gebruik de pijltjestoetsen om te bladeren.`
      );
    };

    const setActiveImage = (requestedIndex, options = {}) => {
      activeIndex = clamp(requestedIndex, 0, images.length - 1);
      const image = images[activeIndex];

      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true');
      });

      thumbs.forEach((thumb, index) => {
        const active = index === activeIndex;
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        thumb.tabIndex = active ? 0 : -1;
      });

      if (mainImage && image) {
        mainImage.src = image.src;
        mainImage.removeAttribute('srcset');
        mainImage.alt = image.alt || '';
      }

      updateIndicators();
      setNavigationState();
      revealActiveThumb(options.instant);

      if (options.scrollMobile !== false && track && slides[activeIndex]) {
        track.scrollTo({
          left: slides[activeIndex].offsetLeft,
          behavior: scrollBehavior(options.instant),
        });
      }

      if (options.focusThumb) {
        thumbs[activeIndex]?.focus({ preventScroll: true });
      }
    };

    const keyboardIndex = (event, index) => {
      if (event.key === 'ArrowRight') return clamp(index + 1, 0, images.length - 1);
      if (event.key === 'ArrowLeft') return clamp(index - 1, 0, images.length - 1);
      if (event.key === 'Home') return 0;
      if (event.key === 'End') return images.length - 1;
      return null;
    };

    gallery.addEventListener('keydown', (event) => {
      if (!gallery.matches(':focus')) return;

      const nextIndex = keyboardIndex(event, activeIndex);
      if (nextIndex === null) return;

      event.preventDefault();
      setActiveImage(nextIndex);
    });

    previousButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setActiveImage(activeIndex - 1);
      });
    });

    nextButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setActiveImage(activeIndex + 1);
      });
    });

    segments.forEach((segment, index) => {
      segment.addEventListener('click', () => {
        setActiveImage(index);
      });
    });

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        setActiveImage(index, { scrollMobile: false });
      });

      thumb.addEventListener('keydown', (event) => {
        const nextIndex = keyboardIndex(event, index);
        if (nextIndex === null) return;

        event.preventDefault();
        setActiveImage(nextIndex, {
          scrollMobile: false,
          focusThumb: true,
        });
      });
    });

    if (track && slides.length > 1) {
      track.addEventListener('scroll', () => {
        if (scrollFrame) return;

        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;
          const midpoint = track.scrollLeft + track.clientWidth / 2;
          let nearestIndex = 0;
          let nearestDistance = Number.POSITIVE_INFINITY;

          slides.forEach((slide, index) => {
            const slideMidpoint = slide.offsetLeft + slide.clientWidth / 2;
            const distance = Math.abs(slideMidpoint - midpoint);

            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = index;
            }
          });

          if (nearestIndex !== activeIndex) {
            setActiveImage(nearestIndex, { scrollMobile: false });
          }
        });
      }, { passive: true });
    }

    gallery.addEventListener('product:media-change', (event) => {
      const index = Number.parseInt(event.detail?.index, 10);
      if (Number.isNaN(index)) return;

      setActiveImage(index, { instant: true });
    });

    /* ------------------------------ */
    /* Compact image viewer           */
    /* ------------------------------ */
    const viewer = gallery.querySelector('[data-gallery-viewer]');

    if (viewer) {
      const viewerImage = viewer.querySelector('[data-gallery-viewer-image]');
      const viewerCounter = viewer.querySelector('[data-gallery-viewer-counter]');
      const viewerMedia = viewer.querySelector('[data-gallery-viewer-media]');
      const closeButton = viewer.querySelector('[data-gallery-viewer-close]');
      const backdrop = viewer.querySelector('[data-gallery-viewer-backdrop]');
      const previousButton = viewer.querySelector('[data-gallery-viewer-prev]');
      const nextButton = viewer.querySelector('[data-gallery-viewer-next]');
      const zoomButton = viewer.querySelector('[data-gallery-viewer-zoom]');
      const zoomLabel = viewer.querySelector('[data-gallery-viewer-zoom-label]');

      let viewerIndex = 0;
      let opener = null;
      let previousBodyOverflow = '';
      let inertRecords = [];

      const setOutsideInert = () => {
        inertRecords = [];
        let current = viewer;

        while (current?.parentElement) {
          const parent = current.parentElement;

          Array.from(parent.children).forEach((sibling) => {
            if (sibling === current) return;

            inertRecords.push({
              element: sibling,
              inert: sibling.inert,
              ariaHidden: sibling.getAttribute('aria-hidden'),
            });

            sibling.inert = true;
            sibling.setAttribute('aria-hidden', 'true');
          });

          if (parent === document.body) break;
          current = parent;
        }
      };

      const restoreOutsideInert = () => {
        inertRecords.forEach(({ element, inert, ariaHidden }) => {
          element.inert = inert;

          if (ariaHidden === null) {
            element.removeAttribute('aria-hidden');
          } else {
            element.setAttribute('aria-hidden', ariaHidden);
          }
        });

        inertRecords = [];
      };

      const resetViewerZoom = () => {
        viewer.classList.remove('is-zoomed');
        zoomButton?.setAttribute('aria-pressed', 'false');
        if (zoomLabel) zoomLabel.textContent = 'Vergroot';
        if (viewerMedia) {
          viewerMedia.scrollTop = 0;
          viewerMedia.scrollLeft = 0;
        }
      };

      const setViewerImage = (requestedIndex) => {
        viewerIndex = clamp(requestedIndex, 0, images.length - 1);
        const image = images[viewerIndex];

        setActiveImage(viewerIndex, { instant: true });
        resetViewerZoom();

        if (viewerImage && image) {
          viewerImage.src = image.src;
          viewerImage.alt = image.alt || '';
          if (image.width) viewerImage.width = image.width;
          if (image.height) viewerImage.height = image.height;
        }

        if (viewerCounter) {
          viewerCounter.textContent = `${viewerIndex + 1} / ${images.length}`;
        }

        if (previousButton) previousButton.disabled = viewerIndex === 0;
        if (nextButton) nextButton.disabled = viewerIndex === images.length - 1;
      };

      const trapViewerFocus = (event) => {
        if (event.key !== 'Tab') return;

        const focusables = visibleFocusables(viewer);
        if (!focusables.length) {
          event.preventDefault();
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      const onViewerKeydown = (event) => {
        if (viewer.hidden) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeViewer();
          return;
        }

        const nextIndex = keyboardIndex(event, viewerIndex);
        if (nextIndex !== null) {
          event.preventDefault();
          event.stopPropagation();
          setViewerImage(nextIndex);
          return;
        }

        trapViewerFocus(event);
      };

      const openViewer = (trigger) => {
        opener = trigger || document.activeElement;
        previousBodyOverflow = document.body.style.overflow;
        viewer.hidden = false;
        document.body.style.overflow = 'hidden';
        setOutsideInert();
        setViewerImage(activeIndex);
        document.addEventListener('keydown', onViewerKeydown, true);
        closeButton?.focus({ preventScroll: true });
      };

      function closeViewer() {
        if (viewer.hidden) return;

        viewer.hidden = true;
        document.body.style.overflow = previousBodyOverflow;
        document.removeEventListener('keydown', onViewerKeydown, true);
        restoreOutsideInert();
        resetViewerZoom();

        if (opener instanceof HTMLElement && opener.isConnected) {
          opener.focus({ preventScroll: true });
        }
      }

      openButtons.forEach((button) => {
        button.addEventListener('click', () => openViewer(button));
      });

      closeButton?.addEventListener('click', closeViewer);
      backdrop?.addEventListener('click', closeViewer);
      previousButton?.addEventListener('click', () => setViewerImage(viewerIndex - 1));
      nextButton?.addEventListener('click', () => setViewerImage(viewerIndex + 1));

      zoomButton?.addEventListener('click', () => {
        const zoomed = !viewer.classList.contains('is-zoomed');
        viewer.classList.toggle('is-zoomed', zoomed);
        zoomButton.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
        if (zoomLabel) zoomLabel.textContent = zoomed ? 'Verklein' : 'Vergroot';

        if (!zoomed && viewerMedia) {
          viewerMedia.scrollTop = 0;
          viewerMedia.scrollLeft = 0;
        }
      });
    }

    setActiveImage(0, { scrollMobile: false, instant: true });

    /*
     * Direct Left/Right navigation on the visible product gallery.
     * No autofocus is used: the normal document focus order stays intact and
     * the product image never receives a visible selection border on load.
     */
    if (images.length > 1) {
      const directGalleryKeys = (event) => {
        if (
          event.defaultPrevented ||
          event.altKey ||
          event.ctrlKey ||
          event.metaKey ||
          event.shiftKey ||
          !['ArrowLeft', 'ArrowRight'].includes(event.key)
        ) {
          return;
        }

        if (viewer && !viewer.hidden) return;
        if (document.querySelector('.overlay.expanded')) return;

        const target = event.target instanceof Element ? event.target : null;
        if (
          target?.closest(
            'a[href], button, input, select, textarea, summary, [contenteditable="true"], [role="dialog"]'
          )
        ) {
          return;
        }

        const rect = gallery.getBoundingClientRect();
        const galleryIsVisible = (
          rect.bottom > 0 &&
          rect.top < window.innerHeight &&
          rect.right > 0 &&
          rect.left < window.innerWidth
        );

        if (!galleryIsVisible) return;

        event.preventDefault();
        setActiveImage(
          activeIndex + (event.key === 'ArrowRight' ? 1 : -1)
        );
      };

      document.addEventListener('keydown', directGalleryKeys);
    }
  });

  /* ------------------------------------------------------------------ */
  /* 2. Product information, variants, quantity and add-to-cart         */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('[data-product-root]').forEach((root) => {
    const dataElement = root.querySelector('[data-product-json]');
    if (!dataElement) return;

    const productData = safeJson(dataElement.textContent, null);
    if (!productData) return;

    const variants = Array.isArray(productData.variants) ? productData.variants : [];
    const labels = productData.labels || {};
    const hasOnlyDefaultVariant = productData.has_only_default_variant === true;
    const form = root.querySelector('[data-product-form] form');
    const optionButtons = Array.from(root.querySelectorAll('[data-option-button]'));
    const selected = {};

    optionButtons.forEach((button) => {
      if (button.getAttribute('data-selected') === 'true') {
        selected[button.getAttribute('data-option-position')] = button.getAttribute('data-value');
      }
    });

    let currentVariant = variants.find(
      (variant) => String(variant.id) === String(productData.selected_variant_id)
    ) || variants[0] || null;

    const priceElement = root.querySelector('[data-price]');
    const comparePriceElement = root.querySelector('[data-compare-price]');
    const availabilityElement = root.querySelector('[data-availability]');
    const availabilityText = root.querySelector('[data-availability-text]');
    const deliveryText = root.querySelector('[data-delivery-text]');
    const deliverySeparator = root.querySelector('[data-delivery-separator]');
    const availableArea = root.querySelector('[data-buy-available]');
    const soldArea = root.querySelector('[data-buy-sold]');
    const soldMessage = root.querySelector('[data-sold-message]');
    const quantityInput = root.querySelector('[data-qty-input]');
    const quantityValue = root.querySelector('[data-qty-value]');
    const decrementButton = root.querySelector('[data-qty-dec]');
    const incrementButton = root.querySelector('[data-qty-inc]');
    const gallery = root.querySelector('[data-gallery]');

    const variantInput = root.querySelector('[data-variant-id]');
    const addButton = root.querySelector('[data-add-button]');

    const exactVariant = () => variants.find((variant) => (
      (variant.options || []).every(
        (value, index) => selected[String(index + 1)] === value
      )
    )) || null;

    const candidateIsAvailable = (position, candidateValue) => variants.some((variant) => {
      if (!variant.available) return false;

      return (variant.options || []).every((value, index) => {
        const optionPosition = String(index + 1);

        if (optionPosition === String(position)) {
          return value === candidateValue;
        }

        const selectedValue = selected[optionPosition];
        return selectedValue === undefined || selectedValue === value;
      });
    });

    const updateOptionButtons = () => {
      optionButtons.forEach((button) => {
        const position = button.getAttribute('data-option-position');
        const value = button.getAttribute('data-value');
        const selectedButton = selected[position] === value;
        const available = candidateIsAvailable(position, value);

        button.setAttribute('data-selected', selectedButton ? 'true' : 'false');
        button.setAttribute('aria-checked', selectedButton ? 'true' : 'false');
        button.setAttribute('data-available', available ? 'true' : 'false');
        button.disabled = !available && !selectedButton;
        button.tabIndex = selectedButton ? 0 : -1;
      });
    };

    const quantityRules = () => {
      const minimum = Math.max(1, Number.parseInt(currentVariant?.quantity_min, 10) || 1);
      const increment = Math.max(1, Number.parseInt(currentVariant?.quantity_increment, 10) || 1);
      const parsedMaximum = Number.parseInt(currentVariant?.quantity_max, 10);
      const maximum = Number.isFinite(parsedMaximum) && parsedMaximum >= minimum
        ? parsedMaximum
        : Number.POSITIVE_INFINITY;

      return { minimum, increment, maximum };
    };

    const syncQuantity = (requestedValue, reset = false) => {
      if (!quantityInput) return;

      const { minimum, increment, maximum } = quantityRules();
      const currentValue = Number.parseInt(quantityInput.value, 10) || minimum;
      const baseValue = reset ? minimum : requestedValue ?? currentValue;
      const steppedValue = minimum + Math.round((baseValue - minimum) / increment) * increment;
      const nextValue = clamp(steppedValue, minimum, maximum);

      quantityInput.value = String(nextValue);
      quantityInput.setAttribute('data-qty-min', String(minimum));
      quantityInput.setAttribute('data-qty-step', String(increment));

      if (Number.isFinite(maximum)) {
        quantityInput.setAttribute('data-qty-max', String(maximum));
      } else {
        quantityInput.removeAttribute('data-qty-max');
      }

      if (quantityValue) quantityValue.textContent = String(nextValue);
      if (decrementButton) decrementButton.disabled = nextValue <= minimum;
      if (incrementButton) incrementButton.disabled = Number.isFinite(maximum) && nextValue >= maximum;
    };

    const updatePrice = (variant) => {
      if (priceElement) {
        priceElement.textContent = variant?.price === 0
          ? (labels.price_on_request || 'Prijs op aanvraag')
          : (variant?.price_formatted || '');
      }

      if (comparePriceElement) {
        const showCompare = Boolean(
          variant &&
          variant.compare_at_price &&
          variant.compare_at_price > variant.price &&
          variant.compare_at_formatted
        );

        comparePriceElement.textContent = showCompare ? variant.compare_at_formatted : '';
        setHidden(comparePriceElement, !showCompare);
      }
    };

    const updateAvailability = (variant) => {
      const validVariant = Boolean(variant);
      const available = Boolean(variant?.available);
      const unavailableCombination = !validVariant;

      root.setAttribute('data-current-available', available ? 'true' : 'false');
      availabilityElement?.classList.toggle('is-sold', !available);

      if (availabilityText) {
        availabilityText.textContent = unavailableCombination
          ? (labels.unavailable || 'Deze combinatie is niet beschikbaar')
          : available
            ? (labels.in_stock || 'Op voorraad')
            : (labels.sold || 'Uitverkocht');
      }

      const showDelivery = available && Boolean(labels.delivery);
      setHidden(deliveryText, !showDelivery);
      setHidden(deliverySeparator, !showDelivery);

      if (deliveryText && showDelivery) {
        deliveryText.textContent = labels.delivery;
      }

      setHidden(availableArea, !available);
      setHidden(soldArea, available);

      root.querySelectorAll('[data-gallery-sold-label]').forEach((label) => {
        setHidden(label, available);
      });

      if (soldMessage) {
        soldMessage.textContent = unavailableCombination
          ? (labels.unavailable || 'Deze combinatie is niet beschikbaar')
          : (labels.sold || 'Uitverkocht');
      }

      if (addButton) {
        addButton.disabled = !available;

        const label = addButton.querySelector('[data-cart-add-label]');
        const icon = addButton.querySelector('[data-cart-add-icon]');

        if (label) {
          label.textContent = available
            ? (labels.add || 'In winkelmand')
            : (labels.sold || 'Uitverkocht');
        }

        if (icon) icon.textContent = available ? '' : '—';
      }
    };

    const updateVariant = (variant, options = {}) => {
      currentVariant = variant;

      if (variantInput) {
        variantInput.value = variant ? String(variant.id) : '';
      }

      updatePrice(variant);
      updateAvailability(variant);

      if (variant?.featured_image_index !== null && variant?.featured_image_index !== undefined) {
        gallery?.dispatchEvent(new CustomEvent('product:media-change', {
          detail: { index: variant.featured_image_index },
        }));
      }

      syncQuantity(undefined, options.resetQuantity !== false);

      if (variant?.id) {
        try {
          const url = new URL(window.location.href);

          if (hasOnlyDefaultVariant) {
            url.searchParams.delete('variant');
          } else {
            url.searchParams.set('variant', variant.id);
          }

          window.history.replaceState(
            {},
            '',
            `${url.pathname}${url.search}${url.hash}`
          );
        } catch (_error) {
          // De URL-update is niet noodzakelijk om het product te bestellen.
        }
      }
    };

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;

        const position = button.getAttribute('data-option-position');
        selected[position] = button.getAttribute('data-value');
        updateOptionButtons();
        updateVariant(exactVariant());
      });

      button.addEventListener('keydown', (event) => {
        const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
        if (!keys.includes(event.key)) return;

        const group = button.closest('[data-option-group]');
        const enabledButtons = Array.from(
          group?.querySelectorAll('[data-option-button]:not(:disabled)') || []
        );

        if (!enabledButtons.length) return;

        event.preventDefault();
        const currentIndex = Math.max(0, enabledButtons.indexOf(button));
        let nextIndex = currentIndex;

        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = enabledButtons.length - 1;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % enabledButtons.length;
        } else {
          nextIndex = (currentIndex - 1 + enabledButtons.length) % enabledButtons.length;
        }

        enabledButtons[nextIndex].focus({ preventScroll: true });
        enabledButtons[nextIndex].click();
      });
    });

    decrementButton?.addEventListener('click', () => {
      if (!quantityInput) return;
      const { increment } = quantityRules();
      syncQuantity((Number.parseInt(quantityInput.value, 10) || 1) - increment);
    });

    incrementButton?.addEventListener('click', () => {
      if (!quantityInput) return;
      const { increment } = quantityRules();
      syncQuantity((Number.parseInt(quantityInput.value, 10) || 1) + increment);
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (!variantInput?.value || root.getAttribute('data-current-available') !== 'true') {
        return;
      }

      const requestedQuantity = Math.max(
        1,
        Number.parseInt(quantityInput?.value, 10) || 1
      );
      const label = form.querySelector('[data-cart-add-label]');
      const icon = form.querySelector('[data-cart-add-icon]');
      const defaultLabel = labels.add || 'In winkelmand';

      try {
        if (window.ZaynaCart && typeof window.ZaynaCart.add === 'function') {
          const addRequest = window.ZaynaCart.add(new FormData(form), {
            form,
            behavior: 'silent',
          });

          if (addButton?.getAttribute('aria-busy') === 'true') {
            if (label) label.textContent = 'Wordt toegevoegd…';
            if (icon) icon.textContent = '';
          }

          await addRequest;

          if (addButton?.classList.contains('is-added')) {
            if (label) {
              label.textContent = requestedQuantity > 1
                ? `${requestedQuantity} toegevoegd`
                : 'Toegevoegd';
            }
            if (icon) icon.textContent = '✓';
            syncQuantity(undefined, true);
          }

          return;
        }

        addButton?.setAttribute('aria-busy', 'true');
        if (addButton) addButton.disabled = true;
        if (label) label.textContent = 'Wordt toegevoegd…';
        if (icon) icon.textContent = '';

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });

        if (!response.ok) {
          throw new Error('Toevoegen aan winkelmand is niet gelukt.');
        }

        const cartResponse = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
        });
        const cartData = cartResponse.ok ? await cartResponse.json() : null;

        if (cartData) {
          document.querySelectorAll('[data-cart-count]').forEach((counter) => {
            counter.textContent = String(cartData.item_count || 0);
            counter.classList.toggle('hidden', (cartData.item_count || 0) <= 0);
          });
        }

        addButton?.classList.add('is-added');
        if (label) {
          label.textContent = requestedQuantity > 1
            ? `${requestedQuantity} toegevoegd`
            : 'Toegevoegd';
        }
        if (icon) icon.textContent = '✓';
        syncQuantity(undefined, true);

        window.setTimeout(() => {
          addButton?.classList.remove('is-added');
          addButton?.removeAttribute('aria-busy');
          if (addButton) addButton.disabled = false;
          if (label) label.textContent = defaultLabel;
          if (icon) icon.textContent = '';
        }, 1200);
      } catch (error) {
        console.error('Zayna product add failed:', error);

        addButton?.classList.add('is-error');
        addButton?.removeAttribute('aria-busy');
        if (addButton) addButton.disabled = false;
        if (label) label.textContent = 'Probeer opnieuw';
        if (icon) icon.textContent = '!';

        window.setTimeout(() => {
          addButton?.classList.remove('is-error');
          if (label) label.textContent = defaultLabel;
          if (icon) icon.textContent = '';
        }, 1800);
      }
    });

    updateOptionButtons();
    updateVariant(currentVariant, { resetQuantity: true });
  });

  /* ------------------------------------------------------------------ */
  /* 3. Related products                                                */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('[data-related-products][data-url]').forEach(async (element) => {
    try {
      const response = await fetch(element.getAttribute('data-url'));
      if (!response.ok) return;

      const parsed = new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );
      const fresh = parsed.querySelector('[data-related-products]');

      if (fresh) element.innerHTML = fresh.innerHTML;
    } catch (error) {
      console.error('Zayna related products load failed:', error);
    }
  });
})();
